import { useMemo, useState, type FC } from 'react';
import {
  Button,
  IconButton,
  InlineLoading,
  InlineNotification,
  Layer,
  ProgressBar,
  Tag,
  TextArea,
} from '@carbon/react';
import { Close } from '@carbon/react/icons';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'components/accordion/accordion';
import type {
  InventoryTaskStatus,
  InventoryWorkspaceTask,
} from 'types/api-responses';
import {
  parseTaskError,
  useSaveInventoryWorkspaceTask,
} from './use-inventory-workspace-tasks';

import style from './inventory-workspace.module.scss';

interface WorkspaceTasksPanelProps {
  workspaceId: number;
  /** Checklist tasks — the gate (`inventory_workspace`) is filtered out upstream. */
  tasks: InventoryWorkspaceTask[];
  completed: number;
  total: number;
  loading: boolean;
  /** Promoted workspaces are read-only; verdicts are locked. */
  promoted: boolean;
  onClose: () => void;
}

interface TaskGroup {
  key: string;
  title: string;
  tasks: InventoryWorkspaceTask[];
}

/** Per-row unsaved edits. Absent = row is showing its persisted state. */
interface Draft {
  verdict: InventoryTaskStatus;
  note: string;
}

// The verdict travels as a status string in the UI but the save takes the
// tri-state boolean the endpoint reads: correct → true, incorrect → false,
// unverified → null.
const statusToCorrectness = (status: InventoryTaskStatus): boolean | null => {
  if (status === 'correct') return true;
  if (status === 'incorrect') return false;
  return null;
};

// Workspace-level verification checklist. Renders the catalog grouped by
// `group`/`groupTitle`, each row carrying a tri-state verdict (Correct/Incorrect,
// re-click reverts to unverified) and a note. The verdict and the note save in
// one PATCH; only the facets that changed are sent (the endpoint reads by key
// presence). Everything is read-only once the workspace is promoted.
export const WorkspaceTasksPanel: FC<WorkspaceTasksPanelProps> = ({
  workspaceId,
  tasks,
  completed,
  total,
  loading,
  promoted,
  onClose,
}) => {
  const save = useSaveInventoryWorkspaceTask(workspaceId);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [saveErrors, setSaveErrors] = useState<Record<string, string>>({});

  const pct = total === 0 ? 0 : (completed / total) * 100;

  // Bucket by group, first-seen order, using groupTitle as the section header.
  const groups = useMemo<TaskGroup[]>(() => {
    const order: string[] = [];
    const byKey = new Map<string, TaskGroup>();
    for (const task of tasks) {
      let group = byKey.get(task.group);
      if (!group) {
        group = { key: task.group, title: task.groupTitle, tasks: [] };
        byKey.set(task.group, group);
        order.push(task.group);
      }
      group.tasks.push(task);
    }
    return order.map((key) => byKey.get(key) as TaskGroup);
  }, [tasks]);

  const setDraft = (task: InventoryWorkspaceTask, patch: Partial<Draft>) => {
    setDrafts((prev) => {
      const current = prev[task.path] ?? {
        verdict: task.status,
        note: task.note ?? '',
      };
      return { ...prev, [task.path]: { ...current, ...patch } };
    });
  };

  const handleSave = async (task: InventoryWorkspaceTask) => {
    const draft = drafts[task.path];
    if (!draft) return;

    const verdictChanged = draft.verdict !== task.status;
    const noteChanged = draft.note !== (task.note ?? '');
    if (!verdictChanged && !noteChanged) return;

    try {
      await save.mutateAsync({
        path: task.path,
        // Only send the facets that changed — presence is meaningful.
        ...(verdictChanged
          ? { correctnessState: statusToCorrectness(draft.verdict) }
          : {}),
        ...(noteChanged ? { reason: draft.note } : {}),
      });
      // Drop the draft so the row reflects the freshly-saved server node.
      setDrafts((prev) => {
        const next = { ...prev };
        delete next[task.path];
        return next;
      });
      setSaveErrors((prev) => {
        const next = { ...prev };
        delete next[task.path];
        return next;
      });
    } catch (error) {
      const message = (await parseTaskError(error)) ?? 'Save failed.';
      setSaveErrors((prev) => ({ ...prev, [task.path]: message }));
    }
  };

  return (
    <div className={style.tasksPanel}>
      <div className={style.tasksPanelHeader}>
        <div>
          <h2 className={style.tasksPanelTitle}>Workspace tasks</h2>
          <p className={style.tasksPanelSubtitle}>
            {`${completed} of ${total} tasks`}
          </p>
        </div>
        <IconButton
          kind="ghost"
          label="Hide tasks"
          align="left"
          onClick={onClose}
        >
          <Close />
        </IconButton>
      </div>

      <div className={style.tasksPanelProgress}>
        <ProgressBar
          value={pct}
          max={100}
          hideLabel
          label="Verification progress"
          size="small"
          status={pct === 100 ? 'finished' : 'active'}
        />
      </div>

      {loading ? (
        <InlineLoading description="Loading workspace tasks…" />
      ) : tasks.length === 0 ? (
        <p className={style.tasksPanelEmpty}>No workspace tasks yet.</p>
      ) : (
        <div className={style.taskGroups}>
          {groups.map((group) => {
            const groupDone = group.tasks.filter(
              (t) => t.status !== null
            ).length;
            return (
              <div key={group.key} className={style.taskGroup}>
                <Accordion defaultExpanded>
                  <AccordionSummary>
                    <div className={style.taskGroupSummary}>
                      <span className={style.taskGroupLabel}>
                        {group.title}
                      </span>
                      <span className={style.taskGroupCount}>
                        {groupDone}/{group.tasks.length}
                      </span>
                    </div>
                  </AccordionSummary>
                  <AccordionDetails>
                    {group.tasks.map((task) => {
                      const draft = drafts[task.path];
                      const verdict = draft ? draft.verdict : task.status;
                      const note = draft ? draft.note : (task.note ?? '');
                      const dirty =
                        !!draft &&
                        (draft.verdict !== task.status ||
                          draft.note !== (task.note ?? ''));
                      const saving =
                        save.isPending && save.variables?.path === task.path;

                      // Re-clicking the active verdict reverts to unverified.
                      const pickVerdict = (next: 'correct' | 'incorrect') =>
                        setDraft(task, {
                          verdict: verdict === next ? null : next,
                        });

                      return (
                        <div key={task.path} className={style.taskRow}>
                          <div className={style.taskRowHead}>
                            <span className={style.taskRowTitle}>
                              {task.title}
                            </span>
                            {task.required ? (
                              <Tag type="cool-gray" size="sm">
                                Required
                              </Tag>
                            ) : null}
                          </div>

                          {task.description ? (
                            <p className={style.taskRowDesc}>
                              {task.description}
                            </p>
                          ) : null}

                          {saveErrors[task.path] ? (
                            <InlineNotification
                              kind="error"
                              title="Save failed"
                              subtitle={saveErrors[task.path]}
                              lowContrast
                              hideCloseButton
                              className={style.taskRowError}
                            />
                          ) : null}

                          <span className={style.taskVerdictLabel}>
                            Verdict
                          </span>
                          <div className={style.taskVerdictButtons}>
                            <Button
                              kind={verdict === 'correct' ? 'primary' : 'ghost'}
                              size="sm"
                              disabled={promoted || saving}
                              onClick={() => pickVerdict('correct')}
                            >
                              Correct
                            </Button>
                            <Button
                              kind={
                                verdict === 'incorrect' ? 'danger' : 'ghost'
                              }
                              size="sm"
                              disabled={promoted || saving}
                              onClick={() => pickVerdict('incorrect')}
                            >
                              Incorrect
                            </Button>
                          </div>

                          {promoted ? (
                            <p className={style.taskLockedHint}>
                              This workspace is promoted; verification is
                              locked.
                            </p>
                          ) : null}

                          <div className={style.taskNote}>
                            <Layer>
                              <TextArea
                                id={`inventory-task-note-${task.path}`}
                                labelText="Note"
                                placeholder={`Add a note about "${task.title}"…`}
                                value={note}
                                disabled={promoted || saving}
                                onChange={(e) =>
                                  setDraft(task, { note: e.target.value })
                                }
                              />
                            </Layer>
                          </div>

                          <div className={style.taskActions}>
                            <Button
                              kind="primary"
                              size="md"
                              disabled={promoted || saving || !dirty}
                              onClick={() => void handleSave(task)}
                            >
                              {saving ? 'Saving…' : 'Save'}
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
